import { ArticleItem } from '../../types';

export const periodicSafetyUpdateReportGuideEn: ArticleItem = {
  slug: "periodic-safety-update-report-guide",
  title: "Periodic Safety Update Report Guideline Released: Post-market Oversight Further Upgraded",
  summary: "The guideline clarifies requirements for medical device Periodic Safety Update Reports, covering vigilance data, product exposure, risk analysis, conclusions, and attachments.",
  seo: {
    title: "Periodic Safety Update Report Guideline Released | HL Medical",
    description: "The Periodic Safety Update Report guideline raises post-market regulatory expectations for medical device vigilance data, risk analysis, and benefit-risk evaluation.",
    keywords: "Periodic Safety Update Report, PSUR, medical device vigilance, post-market surveillance, medical device risk management",
    canonicalUrl: "https://hlmedicalconsult.cn/en/articles/periodic-safety-update-report-guide",
    alternateUrls: {
      en: "https://hlmedicalconsult.cn/en/articles/periodic-safety-update-report-guide",
      cn: "https://hlmedicalconsult.cn/cn/articles/periodic-safety-update-report-guide",
      xDefault: "https://hlmedicalconsult.cn/en/articles/periodic-safety-update-report-guide"
    },
    ogTitle: "Periodic Safety Update Report Guideline Released",
    ogDescription: "The guideline further upgrades post-market oversight and raises expectations for medical device vigilance management.",
    ogType: "article"
  },
  date: "2026-05-08",
  category: "Post-market Surveillance",
  readTime: "5 min read",
  sourceName: "HIGH-USABILITY",
  originalUrl: "https://www.highusability.com",
  sourceNote: "source: High Usability: www.high-usability.com",
  content: [
    { type: "section", text: "01" },
    { type: "heading", text: "Reporting Requirements" },
    { type: "paragraph", text: "The guideline makes clear that the Periodic Safety Update Report should cover all product vigilance data to ensure completeness and continuity of information." },
    { type: "paragraph", text: "For newly registered Class II and Class III medical devices, companies should submit reports annually. For renewal registration products, companies should complete an overall summary at the end of the registration cycle." },
    { type: "paragraph", text: "Reports should be written in Chinese and submitted through the national adverse event monitoring system or retained for inspection." },
    { type: "paragraph", text: "In terms of format, the report consists of a cover page, table of contents, and main body. The cover highlights the responsible entity and vigilance contact information; the table of contents should be detailed to three levels; and the main body should focus on core safety information, emphasizing standardization and traceability." },
    { type: "section", text: "02" },
    { type: "heading", text: "Core Content" },
    { type: "bullet", text: "Basic product information: This section defines the evaluation object and covers medical device name, model specifications, registration certificate information, structure and composition, scope of application, and changes." },
    { type: "bullet", text: "Marketing status: By collecting sales volume, usage volume, or other applicable indicators across different regions and time periods, companies can evaluate actual product use scale and patient exposure." },
    { type: "bullet", text: "Summary analysis of vigilance data: As the core module of the report, this section should systematically organize and analyze vigilance data collected domestically and overseas." },
    { type: "bullet", text: "Product issue analysis: Calculate occurrence rates by product problem, such as malfunction or defect." },
    { type: "bullet", text: "Adverse event distribution: Analyze adverse events by harm manifestation." },
    { type: "bullet", text: "Root cause analysis: List proportions by different investigation conclusions or event root causes." },
    { type: "bullet", text: "Trend analysis: Identify abnormal changes and explain control measures taken due to safety issues." },
    { type: "bullet", text: "Other risk information: Include other evidence related to product safety, such as literature research, domestic and overseas regulatory alerts, PMCF or real-world study results, and updates to risk management documents." },
    { type: "bullet", text: "Standards and regulatory changes: Analyze the potential impact of changes in industry standards or regulations on product safety." },
    { type: "bullet", text: "Conclusion: Based on the data and information above, comprehensively evaluate product safety during the reporting period." },
    { type: "bullet", text: "Benefit-risk assessment: Focus on changes in adverse event occurrence rate and severity, controllability of risk causes, effectiveness of risk control measures, and whether the overall benefit-risk balance has changed." },
    { type: "bullet", text: "Vigilance plan adjustment: Clarify whether the vigilance plan needs to be adjusted." },
    { type: "bullet", text: "Attachments: Include registration approval documents, instructions for use, references, and other necessary supporting materials to ensure report completeness and traceability." },
    { type: "section", text: "03" },
    { type: "heading", text: "Impact on Companies" },
    { type: "paragraph", text: "Implementation of the guideline will place higher requirements on medical device companies. On one hand, companies need to establish a sound vigilance data collection and management system. On the other hand, they also need to improve statistical analysis and risk evaluation capabilities." },
    { type: "paragraph", text: "Report quality will directly affect regulatory communication and the compliance of continued product marketing." }
  ]
};

export const periodicSafetyUpdateReportGuideCn: ArticleItem = {
  slug: "periodic-safety-update-report-guide",
  title: "定期安全更新报告指南发布，上市后监管再升级",
  summary: "指南明确《定期安全更新报告》的撰写要求和核心内容，对医疗器械上市后警戒数据管理与风险评价提出更高要求。",
  seo: {
    title: "定期安全更新报告指南发布，上市后监管再升级 | 赫莱医药",
    description: "定期安全更新报告指南明确产品警戒数据、上市情况、风险分析、结论和附件要求，推动医疗器械上市后监管升级。",
    keywords: "定期安全更新报告, 医疗器械警戒, 上市后监管, 医疗器械风险管理, 不良事件监测",
    canonicalUrl: "https://hlmedicalconsult.cn/cn/articles/periodic-safety-update-report-guide",
    alternateUrls: {
      en: "https://hlmedicalconsult.cn/en/articles/periodic-safety-update-report-guide",
      cn: "https://hlmedicalconsult.cn/cn/articles/periodic-safety-update-report-guide",
      xDefault: "https://hlmedicalconsult.cn/en/articles/periodic-safety-update-report-guide"
    },
    ogTitle: "定期安全更新报告指南发布，上市后监管再升级",
    ogDescription: "指南对医疗器械企业警戒数据收集、统计分析与风险评价能力提出更高要求。",
    ogType: "article"
  },
  date: "2026-05-08",
  category: "上市后监管",
  readTime: "约5分钟",
  sourceName: "HIGH-USABILITY",
  originalUrl: "https://www.highusability.com",
  sourceNote: "source: High Usability: www.high-usability.com",
  content: [
    { type: "section", text: "01" },
    { type: "heading", text: "报告要求" },
    { type: "paragraph", text: "指南明确，《定期安全更新报告》应覆盖产品全部警戒数据，确保信息的完整性与连续性。" },
    { type: "paragraph", text: "对于首次注册的第二、三类医疗器械，企业需按年度提交报告；而延续注册产品，则需在注册周期结束时完成整体汇总。" },
    { type: "paragraph", text: "报告统一采用中文撰写，并通过国家不良事件监测系统提交或留存备查。" },
    { type: "paragraph", text: "在撰写形式上，报告由封面、目录及正文构成。其中，封面信息强调责任主体与警戒联系方式，目录应细化至三级结构，正文则围绕核心安全信息展开，突出规范性与可追溯性。" },
    { type: "section", text: "02" },
    { type: "heading", text: "核心内容" },
    { type: "bullet", text: "产品基本信息：该部分用于界定评价对象，涵盖医疗器械名称、型号规格、注册证信息、结构组成、适用范围及变更情况等基础数据。" },
    { type: "bullet", text: "上市情况：通过对产品在不同地区和时间维度的销售量、使用量或其他适用指标进行统计，评估产品的实际使用规模及患者暴露水平。" },
    { type: "bullet", text: "警戒数据汇总分析：作为报告的核心模块，应对境内外收集的警戒数据进行系统整理与分析。" },
    { type: "bullet", text: "按产品问题（如故障/缺陷）统计发生率。" },
    { type: "bullet", text: "按伤害表现分析不良事件分布。" },
    { type: "bullet", text: "按不同调查结论（事件根本原因）列出占比。" },
    { type: "bullet", text: "开展趋势分析，识别异常变化。" },
    { type: "bullet", text: "说明因安全性问题而采取的控制措施。" },
    { type: "bullet", text: "其他风险信息：补充与产品安全性相关的其他证据，包括文献研究、国内外监管警示、上市后临床跟踪（PMCF）或真实世界研究结果，以及风险管理文件更新情况等。" },
    { type: "bullet", text: "标准与法规变化：分析行业标准或法规变化对产品安全性的潜在影响。" },
    { type: "bullet", text: "结论：基于上述数据与信息，对本报告期内产品的安全性进行综合评价。" },
    { type: "bullet", text: "受益-风险评价：重点关注不良事件发生率及严重程度变化、风险原因的可控性、风险控制措施的有效性，以及整体受益-风险平衡是否发生改变。" },
    { type: "bullet", text: "警戒计划调整：明确是否需要调整警戒计划。" },
    { type: "bullet", text: "附件：包括注册批准文件、产品说明书、参考文献及其他必要证明材料，确保报告完整可追溯。" },
    { type: "section", text: "03" },
    { type: "heading", text: "企业影响" },
    { type: "paragraph", text: "该指南的实施，将对医疗器械企业提出更高要求：一方面，需要建立完善的警戒数据收集与管理体系；另一方面，也需提升统计分析与风险评估能力。" },
    { type: "paragraph", text: "同时，报告质量将直接影响监管沟通及产品持续上市的合规性。" }
  ]
};
